import InvoicePartyCard from '@/components/invoices/InvoicePartyCard'
import InvoicePaymentsTable from '@/components/invoices/InvoicePaymentsTable'
import InvoiceStateBadge from '@/components/invoices/InvoiceStateBadge'
import InvoiceTimeline from '@/components/invoices/InvoiceTimeline'
import PaymentModal from '@/components/invoices/PaymentModal'
import { useInvoiceActions } from '@/hooks/useInvoiceActions'
import { useInvoiceLiveStatus } from '@/hooks/useInvoiceLiveStatus'
import { Invoice, InvoicePayment, InvoiceWithContract, SharedProps, VatBreakdownGroup } from '@/types'
import { formatCurrency, formatInvoiceReason, formatVatRate } from '@/utils/format'
import { Head, Link, router, usePage } from '@inertiajs/react'
import {
  Alert,
  Anchor,
  Button,
  Grid,
  Group,
  Paper,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Title,
  UnstyledButton,
} from '@mantine/core'
import {
  IconAlertTriangle,
  IconArrowBackUp,
  IconCash,
  IconChevronLeft,
  IconCode,
  IconFileTypePdf,
  IconRefresh,
  IconSend,
} from '@tabler/icons-react'
import dayjs from 'dayjs'

function Figure({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div>
      <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
        {label}
      </Text>
      <Text fw={600} c={color}>
        {value}
      </Text>
    </div>
  )
}

function StateAlert({ invoice }: { invoice: Invoice }) {
  const reasonEvent = [...(invoice.events ?? [])]
    .reverse()
    .find((event) => event.status_code === invoice.status_code && event.reason)
  const reason = reasonEvent?.reason ? formatInvoiceReason(reasonEvent.reason) : null

  switch (invoice.state) {
    case 'failed':
      return (
        <Alert variant="light" color="red" icon={<IconAlertTriangle />} title="La plateforme n'a pas reçu la facture">
          {invoice.last_error} Corrigez la cause (contrat, identité de l'entreprise) puis renvoyez-la. Après une panne
          de la plateforme, l'envoi est retenté automatiquement pendant 24 heures.
        </Alert>
      )
    case 'rejected':
      return (
        <Alert variant="light" color="red" icon={<IconAlertTriangle />} title="Facture rejetée">
          {reason ?? 'La facture a été rejetée par la chaîne de transmission.'} Son numéro reste attribué : annulez-la
          puis refaites-la, ses dépôts redeviennent facturables.
        </Alert>
      )
    case 'refused':
      return (
        <Alert variant="light" color="red" icon={<IconAlertTriangle />} title="Facture refusée par le client">
          {reason ?? 'Le client a refusé la facture.'} Annulez-la puis refaites-la une fois le désaccord réglé.
        </Alert>
      )
    case 'disputed':
      return (
        <Alert variant="light" color="orange" icon={<IconAlertTriangle />} title="Le client conteste la facture">
          {reason ?? invoice.status_text} Prenez contact avec lui pour la régler.
        </Alert>
      )
    case 'sending':
      return (
        <Alert variant="light" color="blue" title="Envoi en cours">
          La facture est en route vers la plateforme agréée. Son statut s'affichera ici.
        </Alert>
      )
    default:
      return null
  }
}

export default function InvoicesShow({
  invoice,
  vat_breakdown,
  reports_payments,
}: {
  invoice: InvoiceWithContract
  vat_breakdown: VatBreakdownGroup[]
  reports_payments: boolean
}) {
  const { current_organization } = usePage<SharedProps>().props
  const basePath = `/organizations/${current_organization?.slug}`
  const { invoicePath, resend, cancelAndRedo, openPdf, payingInvoice, startPayment, closePayment } =
    useInvoiceActions()
  const pendingStep = useInvoiceLiveStatus(invoice)

  const handleBack = () => router.get(`${basePath}/invoices`)
  const handlePay = () => startPayment(invoice)
  const handleResend = () => resend(invoice)
  const handleCancelAndRedo = () => cancelAndRedo(invoice)
  const handleOpenPdf = () => openPdf(invoice)
  const handleSynchronize = () => router.post(`${basePath}/e_invoicing_synchronization`)

  const handleCancelPayment = (payment: InvoicePayment) => {
    const message = payment.reported_at
      ? "Annuler ce paiement ? Son encaissement a déjà été déclaré : une annulation sera déclarée à la plateforme."
      : 'Annuler ce paiement ?'
    if (!confirm(message)) return
    router.delete(`${invoicePath(invoice)}/payments/${payment.id}`)
  }

  const period = invoice.period_start ? dayjs(invoice.period_start).format('MMMM YYYY') : null
  const lines = invoice.lines ?? []

  return (
    <>
      <Head title={`Facture ${invoice.number} - ${current_organization?.name}`} />

      <Stack gap="md">
        <UnstyledButton onClick={handleBack} w="fit-content">
          <Group gap={4} c="dimmed">
            <IconChevronLeft size={18} />
            <Text size="sm">Factures</Text>
          </Group>
        </UnstyledButton>

        <Group justify="space-between" align="flex-start">
          <Stack gap={4}>
            <Group gap="sm">
              <Title order={3}>Facture {invoice.number}</Title>
              <InvoiceStateBadge state={invoice.state} size="lg" />
            </Group>
            <Text c="dimmed">
              <Anchor component={Link} href={`${basePath}/contracts/${invoice.contract.id}`}>
                {invoice.contract.customer_name}
              </Anchor>
              {period && <span style={{ textTransform: 'capitalize' }}>{` · ${period}`}</span>}
            </Text>
          </Stack>
          <Group gap="sm">
            {invoice.payable && (
              <Button color="green" leftSection={<IconCash size={16} />} onClick={handlePay}>
                Marquer payé
              </Button>
            )}
            {invoice.resendable && (
              <Button leftSection={<IconSend size={16} />} onClick={handleResend}>
                Renvoyer
              </Button>
            )}
            {invoice.cancellable && (
              <Button variant="light" color="red" leftSection={<IconArrowBackUp size={16} />} onClick={handleCancelAndRedo}>
                Annuler et refaire
              </Button>
            )}
            {invoice.platform_invoice_id && (
              <Button variant="default" leftSection={<IconFileTypePdf size={16} />} onClick={handleOpenPdf}>
                PDF
              </Button>
            )}
            <Button
              variant="default"
              component="a"
              href={`${invoicePath(invoice)}/document.xml`}
              leftSection={<IconCode size={16} />}
            >
              XML
            </Button>
          </Group>
        </Group>

        <StateAlert invoice={invoice} />

        <Paper withBorder radius="md" p="md">
          <SimpleGrid cols={{ base: 2, sm: 4, lg: 7 }}>
            <Figure label="Émise le" value={dayjs(invoice.issued_on).format('DD/MM/YYYY')} />
            <Figure
              label="Échéance"
              value={invoice.due_on ? dayjs(invoice.due_on).format('DD/MM/YYYY') : '—'}
              color={invoice.overdue ? 'orange.8' : undefined}
            />
            <Figure label="Total HT" value={formatCurrency(invoice.total_excluding_tax)} />
            <Figure label="TVA" value={formatCurrency(invoice.total_tax)} />
            <Figure label="Total TTC" value={formatCurrency(invoice.total_including_tax)} />
            <Figure label="Encaissé" value={formatCurrency(invoice.amount_paid)} color="green.8" />
            <Figure label="Reste dû" value={invoice.cancelled_at ? '—' : formatCurrency(invoice.amount_due)} />
          </SimpleGrid>
        </Paper>

        <SimpleGrid cols={{ base: 1, sm: 2 }}>
          <InvoicePartyCard title="Émetteur" party={invoice.parties.seller} />
          <InvoicePartyCard title="Client" party={invoice.parties.buyer} />
        </SimpleGrid>

        <Grid gutter="md">
          <Grid.Col span={{ base: 12, lg: 8 }}>
            <Stack gap="md">
              <Paper withBorder radius="md" p="md">
                <Title order={5} mb="sm">
                  Lignes
                </Title>
                <Table.ScrollContainer minWidth={720}>
                  <Table>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Ticket</Table.Th>
                        <Table.Th>Dépôt</Table.Th>
                        <Table.Th>Réalisation</Table.Th>
                        <Table.Th>Libellé</Table.Th>
                        <Table.Th ta="right">PU HT</Table.Th>
                        <Table.Th ta="right">TVA</Table.Th>
                        <Table.Th ta="right">Total HT</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {lines.map((line) => (
                        <Table.Tr key={line.id}>
                          <Table.Td>
                            {line.order_id ? (
                              <Anchor component={Link} href={`${basePath}/orders/${line.order_id}`}>
                                #{line.order_id}
                              </Anchor>
                            ) : (
                              '—'
                            )}
                          </Table.Td>
                          <Table.Td>{line.deposited_on ? dayjs(line.deposited_on).format('DD/MM/YYYY') : '—'}</Table.Td>
                          <Table.Td>{line.completed_on ? dayjs(line.completed_on).format('DD/MM/YYYY') : '—'}</Table.Td>
                          <Table.Td>{line.label}</Table.Td>
                          <Table.Td ta="right">{formatCurrency(line.unit_price_excluding_tax)}</Table.Td>
                          <Table.Td ta="right">{formatVatRate(line.vat_rate)}</Table.Td>
                          <Table.Td ta="right">{formatCurrency(line.total_excluding_tax)}</Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                    <Table.Tfoot>
                      {vat_breakdown.map((group) => (
                        <Table.Tr key={group.rate}>
                          <Table.Td colSpan={6} ta="right" c="dimmed">
                            {`TVA ${formatVatRate(group.rate)} sur ${formatCurrency(group.basis)}`}
                          </Table.Td>
                          <Table.Td ta="right">{formatCurrency(group.tax)}</Table.Td>
                        </Table.Tr>
                      ))}
                      <Table.Tr>
                        <Table.Td colSpan={6} ta="right" fw={700}>
                          Total TTC
                        </Table.Td>
                        <Table.Td ta="right" fw={700}>
                          {formatCurrency(invoice.total_including_tax)}
                        </Table.Td>
                      </Table.Tr>
                    </Table.Tfoot>
                  </Table>
                </Table.ScrollContainer>
              </Paper>

              <Paper withBorder radius="md" p="md">
                <Group justify="space-between" mb="sm">
                  <Title order={5}>Paiements</Title>
                  {invoice.payable && (
                    <Button size="xs" variant="light" color="green" onClick={handlePay}>
                      Ajouter un paiement
                    </Button>
                  )}
                </Group>
                <InvoicePaymentsTable
                  payments={invoice.payments ?? []}
                  reportsPayments={reports_payments}
                  onCancel={handleCancelPayment}
                />
              </Paper>
            </Stack>
          </Grid.Col>

          <Grid.Col span={{ base: 12, lg: 4 }}>
            <Paper withBorder radius="md" p="md">
              <Group justify="space-between" mb="sm">
                <Title order={5}>Suivi</Title>
                <Button size="xs" variant="subtle" leftSection={<IconRefresh size={14} />} onClick={handleSynchronize}>
                  Actualiser
                </Button>
              </Group>
              <InvoiceTimeline events={invoice.events ?? []} pendingLabel={pendingStep} />
            </Paper>
          </Grid.Col>
        </Grid>
      </Stack>

      <PaymentModal invoice={payingInvoice} onClose={closePayment} />
    </>
  )
}
